import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Paragraph } from '@actual-app/components/paragraph';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import type { ImportAccountBalancesResult } from '@actual-app/core/server/accounts/app';

import { useImportAccountBalancesMutation } from '#accounts';
import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';

type ImportAccountBalancesModalProps = {
  filename: string;
  onImported: (didChange: boolean) => void;
};

export function ImportAccountBalancesModal({
  filename,
  onImported,
}: ImportAccountBalancesModalProps) {
  const { t } = useTranslation();
  const [result, setResult] = useState<ImportAccountBalancesResult | null>(
    null,
  );

  const { isPending, mutateAsync } = useImportAccountBalancesMutation();

  useEffect(() => {
    let isMounted = true;

    void mutateAsync({ filepath: filename })
      .then(response => {
        if (!isMounted) {
          return;
        }

        setResult(response);
        onImported(response.updatedAccounts.length > 0);
      })
      .catch(() => {
        // Error handling is done in the mutation hook.
      });

    return () => {
      isMounted = false;
    };
  }, [filename, mutateAsync, onImported]);

  const rows = result?.rows ?? [];
  const successCount = rows.filter(row => !row.error).length;
  const errorRows = rows.filter(row => row.error);

  return (
    <Modal
      name="import-account-balances"
      isLoading={isPending}
      containerProps={{ style: { width: 520 } }}
    >
      {({ state }) => (
        <>
          <ModalHeader
            title={t('Import Account Balances')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />

          <View>
            <Paragraph>
              <Text>
                <Trans>Imported file</Trans>: {filename}
              </Text>
            </Paragraph>

            {isPending ? (
              <Paragraph>
                <Trans>Importing balances and reconciling accounts...</Trans>
              </Paragraph>
            ) : (
              <View style={{ gap: 12 }}>
                <Paragraph>
                  <Text>
                    <Trans>Updated</Trans> {successCount} {t('account(s)')}.{' '}
                    <Trans>Found</Trans> {errorRows.length}{' '}
                    {t('row error(s)').toLowerCase()}.
                  </Text>
                </Paragraph>

                {errorRows.length > 0 && (
                  <View style={{ gap: 6 }}>
                    {errorRows.map(row => (
                      <Text key={`row-${row.rowNumber}`}>
                        <Trans>Row</Trans> {row.rowNumber} (
                        {row.accountName || t('(empty)')}): {row.error}
                      </Text>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>
        </>
      )}
    </Modal>
  );
}
